// ─────────────────────────────────────────────────────────────────────────────
// Minimal DOM helpers for the mobile app.
//
// ⚠ DELIBERATE DEVIATION FROM THE PORTAL'S UI CONVENTION, stated here so it is
// a decision rather than an oversight.
//
// CLAUDE.md's 3-layer rules say event wiring uses jQuery `.on()` closures. That
// convention exists for the portal's pages, which already load jQuery and UIkit
// for everything else they do. This bundle is served to a phone from a different
// host, with its own stylesheet, and shipping ~120 KB of desktop framework to
// render two screens would contradict the same reasoning that keeps
// `default.scss` off this app.
//
// What IS kept, because it is what the rule protects:
//   • no `window` globals for dispatch,
//   • no `onclick`/`onchange` attributes in markup,
//   • every listener registered inside a module's `initUI*` function, in a
//     closure over its own state.
//
// ⚠ TEXT IS SET WITH textContent, NEVER innerHTML. Todo titles, comments,
// project names and people's names come out of a CUSTOMER'S OWN TENANT — they
// are user-authored strings from a system this app does not control. `html()`
// below exists only for our own icon constants and says so.
// ─────────────────────────────────────────────────────────────────────────────

type Attributes = Record<string, string | number | boolean | null | undefined>;

/**
 * Build an element.
 *
 * `text` goes through textContent. Children may be elements or strings; strings
 * become text nodes, so nothing a caller passes can become markup by accident.
 */
export function el<K extends keyof HTMLElementTagNameMap>(
    tag: K,
    attributes: Attributes = {},
    children: Array<Node | string> | string = [],
): HTMLElementTagNameMap[K] {
    const node = document.createElement(tag);

    for (const [key, value] of Object.entries(attributes)) {
        if (value === null || value === undefined || value === false) continue;
        if (key === "class") node.className = String(value);
        else if (key === "text") node.textContent = String(value);
        else if (key === "style") node.setAttribute("style", String(value));
        else if (value === true) node.setAttribute(key, "");
        else node.setAttribute(key, String(value));
    }

    const list = typeof children === "string" ? [children] : children;
    for (const child of list) {
        node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
    }

    return node;
}

/**
 * An element whose content is a trusted markup CONSTANT from this codebase.
 *
 * The only legitimate caller is `icons.ts`. Never hand this a value that came
 * from a response, a form field, or a tenant.
 */
export function trustedSvg(markup: string, className?: string): HTMLSpanElement {
    const span = document.createElement("span");
    if (className) span.className = className;
    span.innerHTML = markup;
    span.style.display = "inline-flex";
    return span;
}

/**
 * Tags a lifecycle remark may carry, and the attributes each may keep.
 *
 * ⚠ THIS IS THE SECOND OF TWO PASSES. `pqfMobileRemarkHtml()` on the server is the boundary and
 * this list is its mirror; the scrub is repeated here because a guarantee that lives only at the
 * other end of a wire is not one this module can state, and this module is the one calling
 * `innerHTML`. Keep the two lists in step — a tag the server allows and this drops is formatting
 * that silently disappears; the reverse is a hole.
 */
const RICH_TEXT_TAGS: Record<string, string[]> = {
    P: ["style"],
    DIV: ["style"],
    BR: [],
    B: ["style"],
    STRONG: ["style"],
    I: ["style"],
    EM: ["style"],
    U: ["style"],
    S: ["style"],
    STRIKE: ["style"],
    SPAN: ["style"],
    UL: ["style"],
    OL: ["style"],
    LI: ["style"],
    BLOCKQUOTE: ["style"],
    CODE: ["style"],
    PRE: ["style"],
    A: ["href", "target", "rel", "style"],
};

/**
 * The `style` properties a comment may carry — an ALLOW-LIST, never a deny-list.
 *
 * Interpreting the HTML means interpreting its styling; `span` exists in the tag list for no other
 * reason. What makes that safe is this list rather than the tags: `style` is a full CSS surface,
 * and `position: fixed` with a `z-index` turns a comment into an overlay over the whole app.
 * Naming the handful of properties that describe TEXT means nothing else can arrive, including a
 * property invented after this was written. Mirrors `pqfMobileInlineStyle()`.
 */
const RICH_TEXT_STYLE_PROPERTIES = [
    "color",
    "background-color",
    "font-size",
    "font-weight",
    "font-style",
    "text-decoration",
    "text-decoration-line",
    "text-align",
];

/** `url(` reaches the network, and the rest are ways a value has smuggled something past before. */
const RICH_TEXT_STYLE_UNSAFE = /url\s*\(|expression\s*\(|@import|[\\<>{}]|\/\*|!important/i;

/** Keep the declarations that describe text, drop the rest. Empty means: remove the attribute. */
function scrubInlineStyle(style: string): string {
    const kept: string[] = [];

    for (const declaration of style.split(";")) {
        const at = declaration.indexOf(":");
        if (at < 0) continue;

        const property = declaration.slice(0, at).trim().toLowerCase();
        const value = declaration.slice(at + 1).trim();

        if (!RICH_TEXT_STYLE_PROPERTIES.includes(property)) continue;
        if (!value || value.length > 64) continue;
        if (RICH_TEXT_STYLE_UNSAFE.test(value)) continue;

        kept.push(`${property}: ${value}`);
    }

    return kept.join("; ");
}

/** Removed WHOLE, never unwrapped: unwrapping a script puts its source on screen as prose. */
const RICH_TEXT_DROP = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "TEMPLATE", "LINK", "META"]);

/**
 * A span holding rich text that came from the tenant.
 *
 * ⚠ THE ONLY REMOTE VALUE IN THIS APP THAT IS EVER `innerHTML`'d, and it earns it: a lifecycle
 * remark IS html — a comment written in the PQFORCE client is rich text — so escaping it puts tags
 * on screen and flattening it throws away formatting somebody applied on purpose.
 *
 * It is parsed into an inert document FIRST (`DOMParser`, which runs nothing and fetches nothing),
 * scrubbed against the allow-list above, and only the surviving nodes are adopted. So no markup
 * from a response is ever handed to a live parser attached to this page.
 *
 * The CSP is the third layer, not the first: `script-src 'self'` blocks inline handlers and
 * `javascript:`, `img-src 'self' data:` blocks a remote beacon. None of that is a reason to skip
 * the scrub — a CSP is a property of the page a future template could weaken.
 */
export function richTextHtml(html: string, className?: string): HTMLSpanElement {
    const span = document.createElement("span");
    if (className) span.className = className;
    if (!html) return span;

    const parsed = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
    scrubRichText(parsed.body);

    while (parsed.body.firstChild) span.appendChild(parsed.body.firstChild);

    return span;
}

/** Depth-first: drop what carries nothing, unwrap what is not allowed, strip stray attributes. */
function scrubRichText(node: Element): void {
    // A snapshot, because removing a child mutates a live child list underneath the loop — which
    // is how a scrubber comes to skip every second node, and the skipped ones are its whole point.
    for (const child of Array.from(node.children)) {
        if (RICH_TEXT_DROP.has(child.tagName)) {
            child.remove();
            continue;
        }

        scrubRichText(child);

        const keep = RICH_TEXT_TAGS[child.tagName];
        if (!keep) {
            // Unwrap, never delete: the text inside an unknown wrapper is somebody's sentence.
            child.replaceWith(...Array.from(child.childNodes));
            continue;
        }

        for (const name of Array.from(child.getAttributeNames())) {
            if (!keep.includes(name.toLowerCase())) child.removeAttribute(name);
        }

        const style = child.getAttribute("style");
        if (style !== null) {
            const kept = scrubInlineStyle(style);
            if (kept) child.setAttribute("style", kept);
            else child.removeAttribute("style");
        }

        // A SPAN is in the tag list ONLY to carry styling; one left with none is unwrapped, so
        // the tree never grows an element that expresses nothing.
        if (child.tagName === "SPAN" && child.attributes.length === 0) {
            child.replaceWith(...Array.from(child.childNodes));
            continue;
        }

        if (child.tagName === "A") {
            const href = (child.getAttribute("href") ?? "").trim();
            // An explicit http/https/mailto scheme and nothing else — which is what refuses
            // `javascript:`, `data:` and the protocol-relative `//evil.com` alike.
            if (!/^(https?|mailto):/i.test(href)) child.removeAttribute("href");
            else {
                child.setAttribute("target", "_blank");
                child.setAttribute("rel", "noopener noreferrer");
            }
        }
    }
}

/** Remove every child. */
export function clear(node: Element): void {
    while (node.firstChild) node.removeChild(node.firstChild);
}

/** Replace an element's children in one go. */
export function fill(node: Element, children: Array<Node | string>): void {
    clear(node);
    for (const child of children) {
        node.appendChild(typeof child === "string" ? document.createTextNode(child) : child);
    }
}

/**
 * Delegated listener.
 *
 * Delegation rather than per-node binding because these lists are re-rendered
 * wholesale on every state change; a listener bound to a row would be thrown
 * away with it, and re-binding on each paint is how leaks start.
 */
export function on<E extends keyof HTMLElementEventMap>(
    root: Element | Document,
    event: E,
    selector: string,
    handler: (element: HTMLElement, event: HTMLElementEventMap[E]) => void,
): () => void {
    const listener = (raw: Event) => {
        const target = raw.target as HTMLElement | null;
        const match = target?.closest<HTMLElement>(selector);
        if (match && root.contains(match)) handler(match, raw as HTMLElementEventMap[E]);
    };
    root.addEventListener(event, listener as EventListener);
    return () => root.removeEventListener(event, listener as EventListener);
}

/** Bind a listener directly, returning its own remover. */
export function bind<E extends keyof HTMLElementEventMap>(
    node: Element,
    event: E,
    handler: (event: HTMLElementEventMap[E]) => void,
): () => void {
    node.addEventListener(event, handler as EventListener);
    return () => node.removeEventListener(event, handler as EventListener);
}
